/**
 * `groupEventsByDay` — pure model that folds an event stream into newest-first
 * day groups for the per-task timeline.
 *
 * The design (Claude-design bundle, 2026-05-25) landed on **newest-first**
 * ordering with a quiet day header ("Today · Tue · May 18") above each day's
 * events, because the most common reason to open a task's history is "what just
 * changed?" — recent events should be one glance away, not a scroll away.
 *
 * Pure + dependency-free (no React, no `Date.now` baked in — `now` is injected)
 * so it is unit-testable in isolation and the rendering stays a thin view.
 * Grouping is done in the team's timezone (`tz`) when supplied, so a day
 * boundary matches the team's local midnight, not the viewer's.
 *
 * Brand firewall (§A9): this only buckets events by day. It carries no actor,
 * no count-about-a-person — just the work's chronology.
 */

interface HasOccurredAt {
  readonly occurred_at: string;
}

/** One day's worth of events, newest-first within the day. */
export interface DayGroup<T extends HasOccurredAt> {
  /** Stable key — the local calendar day, "YYYY-MM-DD" (or "undated"). */
  readonly key: string;
  /** Relative label: "Today" / "Yesterday" / a weekday / an absolute date. */
  readonly label: string;
  /** Absolute sub-label: "Tue · May 18" (omitted for the undated bucket). */
  readonly dateLabel: string;
  /** The day's events, newest `occurred_at` first. */
  readonly events: T[];
}

export interface GroupByDayOptions {
  /** IANA zone (e.g. "Asia/Singapore") to bucket days in; omit for the viewer's. */
  readonly tz?: string | undefined;
  /** Injected "now" for deterministic tests; defaults to the current instant. */
  readonly now?: Date;
}

function ms(iso: string): number {
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? Number.NaN : t;
}

/** The local calendar-day key ("YYYY-MM-DD") for an instant, in `tz`. */
function dayKey(d: Date, tz: string | undefined): string {
  // en-CA renders ISO-ordered "YYYY-MM-DD", a clean, locale-stable day key.
  return d.toLocaleDateString("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

/** "Tue · May 18" — the absolute sub-label. */
function dateLabel(d: Date, tz: string | undefined): string {
  const weekday = d.toLocaleDateString(undefined, { weekday: "short", timeZone: tz });
  const md = d.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: tz });
  return `${weekday} · ${md}`;
}

/**
 * The relative label for a day, given its key and today's / yesterday's keys.
 * "Today" / "Yesterday", else the weekday name when within the last 6 days
 * (so a glance reads "Friday"), else a plain absolute date.
 */
function relativeLabel(
  d: Date,
  key: string,
  todayKey: string,
  yesterdayKey: string,
  daysAgo: number,
  tz: string | undefined,
): string {
  if (key === todayKey) return "Today";
  if (key === yesterdayKey) return "Yesterday";
  if (daysAgo >= 2 && daysAgo <= 6) {
    return d.toLocaleDateString(undefined, { weekday: "long", timeZone: tz });
  }
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: tz });
}

/**
 * Fold events into newest-first day groups. Events with an unparseable
 * `occurred_at` fall into a single trailing "undated" bucket rather than
 * throwing. Input order is irrelevant — the result is always newest-first,
 * both across days and within each day.
 */
export function groupEventsByDay<T extends HasOccurredAt>(
  events: readonly T[],
  options: GroupByDayOptions = {},
): DayGroup<T>[] {
  const { tz, now = new Date() } = options;

  const todayKey = dayKey(now, tz);
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const yesterdayKey = dayKey(yesterday, tz);
  const startOfTodayKeyMs = (() => {
    // Midnight of "today" used only to bucket the coarse "days ago" for labels.
    const t = new Date(now);
    t.setHours(0, 0, 0, 0);
    return t.getTime();
  })();

  // Newest-first across the whole stream; a defensive copy (input is readonly).
  const sorted = [...events].sort((a, b) => {
    const ta = ms(a.occurred_at);
    const tb = ms(b.occurred_at);
    if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
    if (Number.isNaN(ta)) return 1; // undated sinks to the end
    if (Number.isNaN(tb)) return -1;
    return tb - ta;
  });

  const groups: DayGroup<T>[] = [];
  const byKey = new Map<string, DayGroup<T>>();

  for (const event of sorted) {
    const t = ms(event.occurred_at);
    if (Number.isNaN(t)) {
      const undated = byKey.get("undated");
      if (undated) {
        (undated.events as T[]).push(event);
      } else {
        const g: DayGroup<T> = { key: "undated", label: "Undated", dateLabel: "", events: [event] };
        byKey.set("undated", g);
        groups.push(g);
      }
      continue;
    }

    const d = new Date(t);
    const key = dayKey(d, tz);
    const existing = byKey.get(key);
    if (existing) {
      (existing.events as T[]).push(event);
      continue;
    }

    const dayStart = new Date(d);
    dayStart.setHours(0, 0, 0, 0);
    const daysAgo = Math.round((startOfTodayKeyMs - dayStart.getTime()) / (24 * 60 * 60 * 1000));
    const group: DayGroup<T> = {
      key,
      label: relativeLabel(d, key, todayKey, yesterdayKey, daysAgo, tz),
      dateLabel: dateLabel(d, tz),
      events: [event],
    };
    byKey.set(key, group);
    groups.push(group);
  }

  return groups;
}
